import {
  type AppError,
  InvalidTokenError,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  AiGenerateEntryTodosOutput,
  type CreateEntryTodoInput,
  CreateEntryTodoOutput,
  DeleteEntryTodoOutput,
  EntryTodo,
  GetEntryTodosOutput,
  TodoRemindersResponse,
  type TodoStatusType,
  type UpdateEntryTodoInput,
  UpdateEntryTodoOutput,
} from '../entities/entryTodos/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

type EntryTodosReturnValueTypeWithOauthToken<T> = ResultWithOauthToken<
  T,
  AppError
>;

export type EntryTodoType = z.infer<typeof EntryTodo>;

// GET /3.0/service/users/me/entry-todos - 一覧取得
type GetEntryTodosArgs = {
  cookies: Partial<Record<string, string>>;
  status?: TodoStatusType;
  isAiGenerated?: boolean;
  priority?: number;
  sortBy?: 'position' | 'dueDate' | 'priority';
  sortOrder?: 'asc' | 'desc';
  version?: string;
};

export type GetEntryTodosResponse = z.infer<typeof GetEntryTodosOutput>;

export type GetEntryTodosReturnValueType =
  EntryTodosReturnValueTypeWithOauthToken<GetEntryTodosResponse>;

type UseGetEntryTodosOptions = {
  config?: UseQueryOptions<GetEntryTodosReturnValueType>;
  isAuthenticated?: boolean;
} & Omit<GetEntryTodosArgs, 'cookies'>;

export const getEntryTodos = async ({
  cookies,
  status,
  isAiGenerated,
  priority,
  sortBy,
  sortOrder,
  version = DEFAULT_API_VERSION,
}: GetEntryTodosArgs): Promise<GetEntryTodosReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const queryParams: Record<string, string | number | boolean> = {};
    if (status !== undefined) queryParams.status = status;
    if (isAiGenerated !== undefined) queryParams.isAiGenerated = isAiGenerated;
    if (priority !== undefined) queryParams.priority = priority;
    if (sortBy !== undefined) queryParams.sortBy = sortBy;
    if (sortOrder !== undefined) queryParams.sortOrder = sortOrder;

    const path = createPathWithQuery(
      `/${version}/users/me/entry-todos`,
      queryParams,
    );

    return defaultClient.get<GetEntryTodosResponse>(path, GetEntryTodosOutput);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useGetEntryTodos = ({
  config,
  status,
  isAiGenerated,
  priority,
  sortBy,
  sortOrder,
  isAuthenticated,
}: UseGetEntryTodosOptions = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<GetEntryTodosReturnValueType>({
    ...config,
    queryKey: [
      'entryTodos',
      status,
      isAiGenerated,
      priority,
      sortBy,
      sortOrder,
      isAuthenticated,
    ],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getEntryTodos({
        cookies,
        status,
        isAiGenerated,
        priority,
        sortBy,
        sortOrder,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

// GET /3.0/service/users/me/entry-todos/{id} - 詳細取得
type GetEntryTodoArgs = {
  cookies: Partial<Record<string, string>>;
  id: number;
  version?: string;
};

export type GetEntryTodoResponse = z.infer<typeof EntryTodo>;

export type GetEntryTodoReturnValueType =
  EntryTodosReturnValueTypeWithOauthToken<GetEntryTodoResponse>;

type UseGetEntryTodoOptions = {
  config?: UseQueryOptions<GetEntryTodoReturnValueType>;
};

export const getEntryTodo = async ({
  cookies,
  id,
  version = DEFAULT_API_VERSION,
}: GetEntryTodoArgs): Promise<GetEntryTodoReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-todos/${id}`;

    return defaultClient.get<GetEntryTodoResponse>(path, EntryTodo);
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useGetEntryTodo = (
  id: number,
  {config}: UseGetEntryTodoOptions = {},
) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<GetEntryTodoReturnValueType>({
    ...config,
    queryKey: ['entryTodo', id],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getEntryTodo({cookies, id});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

// POST /3.0/service/users/me/entry-todos - 作成
type CreateEntryTodoArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
} & z.infer<typeof CreateEntryTodoInput>;

export type CreateEntryTodoResponse = z.infer<typeof CreateEntryTodoOutput>;

export type CreateEntryTodoReturnValueType =
  EntryTodosReturnValueTypeWithOauthToken<CreateEntryTodoResponse>;

export const createEntryTodo = async ({
  cookies,
  companyID,
  companyName,
  userEntryManagementID,
  userEntryManagementStepID,
  title,
  link,
  body,
  dueDate,
  priority,
  isAiGenerated,
  version = DEFAULT_API_VERSION,
}: CreateEntryTodoArgs): Promise<CreateEntryTodoReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-todos`;

    const requestBody = {
      title,
      ...(companyID !== undefined && {companyID}),
      ...(companyName !== undefined && {companyName}),
      ...(userEntryManagementID !== undefined && {userEntryManagementID}),
      ...(userEntryManagementStepID !== undefined && {
        userEntryManagementStepID,
      }),
      ...(link !== undefined && {link}),
      ...(body !== undefined && {body}),
      ...(dueDate !== undefined && {dueDate}),
      ...(priority !== undefined && {priority}),
      ...(isAiGenerated !== undefined && {isAiGenerated}),
    };

    return defaultClient.post<CreateEntryTodoResponse, typeof requestBody>(
      path,
      CreateEntryTodoOutput,
      requestBody,
    );
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useCreateEntryTodo = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    CreateEntryTodoReturnValueType,
    Error,
    Omit<CreateEntryTodoArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await createEntryTodo({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// PUT /3.0/service/users/me/entry-todos/{id} - 更新
type UpdateEntryTodoArgs = {
  cookies: Partial<Record<string, string>>;
  id: number;
  version?: string;
} & z.infer<typeof UpdateEntryTodoInput>;

export type UpdateEntryTodoResponse = z.infer<typeof UpdateEntryTodoOutput>;

export type UpdateEntryTodoReturnValueType =
  EntryTodosReturnValueTypeWithOauthToken<UpdateEntryTodoResponse>;

export const updateEntryTodo = async ({
  cookies,
  id,
  companyID,
  companyName,
  userEntryManagementID,
  userEntryManagementStepID,
  status,
  title,
  link,
  body,
  dueDate,
  priority,
  position,
  targetStatus,
  previousTodoID,
  nextTodoID,
  isEdited,
  version = DEFAULT_API_VERSION,
}: UpdateEntryTodoArgs): Promise<UpdateEntryTodoReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-todos/${id}`;

    const requestBody: Record<
      string,
      string | number | boolean | null | undefined
    > = {};
    if (companyID !== undefined) requestBody.companyID = companyID;
    if (companyName !== undefined) requestBody.companyName = companyName;
    if (userEntryManagementID !== undefined)
      requestBody.userEntryManagementID = userEntryManagementID;
    if (userEntryManagementStepID !== undefined)
      requestBody.userEntryManagementStepID = userEntryManagementStepID;
    if (status !== undefined) requestBody.status = status;
    if (title !== undefined) requestBody.title = title;
    if (link !== undefined) requestBody.link = link;
    if (body !== undefined) requestBody.body = body;
    if (dueDate !== undefined) requestBody.dueDate = dueDate;
    if (priority !== undefined) requestBody.priority = priority;
    if (position !== undefined) requestBody.position = position;
    if (targetStatus !== undefined) requestBody.targetStatus = targetStatus;
    if (previousTodoID !== undefined)
      requestBody.previousTodoID = previousTodoID;
    if (nextTodoID !== undefined) requestBody.nextTodoID = nextTodoID;
    if (isEdited !== undefined) requestBody.isEdited = isEdited;

    return defaultClient.put<UpdateEntryTodoResponse, typeof requestBody>(
      path,
      UpdateEntryTodoOutput,
      requestBody,
    );
  };

  return sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });
};

export const useUpdateEntryTodo = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    UpdateEntryTodoReturnValueType,
    Error,
    Omit<UpdateEntryTodoArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await updateEntryTodo({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// DELETE /3.0/service/users/me/entry-todos/{id} - 削除
type DeleteEntryTodoArgs = {
  cookies: Partial<Record<string, string>>;
  id: number;
  version?: string;
};

export type DeleteEntryTodoResponse = z.infer<typeof DeleteEntryTodoOutput>;

export type DeleteEntryTodoReturnValueType =
  EntryTodosReturnValueTypeWithOauthToken<DeleteEntryTodoResponse>;

export const deleteEntryTodo = async ({
  cookies,
  id,
  version = DEFAULT_API_VERSION,
}: DeleteEntryTodoArgs): Promise<DeleteEntryTodoReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-todos/${id}`;

    return defaultClient.delete<DeleteEntryTodoResponse>(
      path,
      DeleteEntryTodoOutput,
    );
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  // 削除APIはGoogle未連携時に204 No Contentを返すため、ApiResponseShapeErrorを成功として扱う
  if (!result.ok && result.error === 'ApiResponseShapeError') {
    return {
      ok: true as const,
      value: {googleSyncResult: undefined},
      accessToken: result.accessToken,
    };
  }

  return result;
};

export const useDeleteEntryTodo = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    DeleteEntryTodoReturnValueType,
    Error,
    Omit<DeleteEntryTodoArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await deleteEntryTodo({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// POST /3.0/service/users/me/entry-todos/ai-generate - AI TODO生成
type AiGenerateEntryTodosArgs = {
  cookies: Partial<Record<string, string>>;
  stepID: number;
  minCount?: number;
  maxCount?: number;
  departmentID?: number;
  selectionPhaseID?: number;
  version?: string;
};

export type AiGenerateEntryTodosResponse = z.infer<
  typeof AiGenerateEntryTodosOutput
>;

export type AiGenerateEntryTodosReturnValueType =
  EntryTodosReturnValueTypeWithOauthToken<AiGenerateEntryTodosResponse>;

export const aiGenerateEntryTodos = async ({
  cookies,
  stepID,
  minCount,
  maxCount,
  departmentID,
  selectionPhaseID,
  version = DEFAULT_API_VERSION,
}: AiGenerateEntryTodosArgs): Promise<AiGenerateEntryTodosReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-todos/ai-generate`;

    const requestBody: Record<string, number | undefined> = {
      stepID,
    };
    if (minCount !== undefined) requestBody.minCount = minCount;
    if (maxCount !== undefined) requestBody.maxCount = maxCount;
    if (departmentID !== undefined) requestBody.departmentID = departmentID;
    if (selectionPhaseID !== undefined)
      requestBody.selectionPhaseID = selectionPhaseID;

    return defaultClient.post<AiGenerateEntryTodosResponse, typeof requestBody>(
      path,
      AiGenerateEntryTodosOutput,
      requestBody,
    );
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useAiGenerateEntryTodos = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useMutation<
    AiGenerateEntryTodosReturnValueType,
    Error,
    Omit<AiGenerateEntryTodosArgs, 'cookies'>
  >({
    async mutationFn(args) {
      const cookies = parseCookies();
      const result = await aiGenerateEntryTodos({...args, cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }
      return result;
    },
  });
};

// GET /3.0/users/me/entry-todos/reminders - TODOリマインダー件数取得
type GetTodoRemindersArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export type GetTodoRemindersResponse = z.infer<typeof TodoRemindersResponse>;

export type GetTodoRemindersReturnValueType =
  EntryTodosReturnValueTypeWithOauthToken<GetTodoRemindersResponse>;

type UseGetTodoRemindersOptions = {
  config?: UseQueryOptions<GetTodoRemindersReturnValueType>;
};

export const getTodoReminders = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetTodoRemindersArgs): Promise<GetTodoRemindersReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entry-todos/reminders`;

    return defaultClient.get<GetTodoRemindersResponse>(
      path,
      TodoRemindersResponse,
    );
  };

  const result = await sendApiRequest({
    cookies,
    request,
    authRequired: true,
    allowReturnError: true,
  });

  return result;
};

export const useGetTodoReminders = ({
  config,
}: UseGetTodoRemindersOptions = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<GetTodoRemindersReturnValueType>({
    ...config,
    queryKey: ['todoReminders'],
    staleTime: 0, // キャッシュを無効化してリアルタイム更新を実現
    refetchOnMount: true, // マウント時に常に再取得
    async queryFn() {
      const cookies = parseCookies();
      const result = await getTodoReminders({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};
